// src/pages/SettingsPage.js
import { useEffect, useMemo, useState, useContext, useRef } from 'react';
import { saveJSON, removeKey } from '../utils/persist';
import AuthContext from '../AuthContext';
import API_BASE from '../apiConfig';
import './SettingsPage.css';
import BackToDashboard from "../components/BackToDashboard";

const USER_SETTINGS_URL = `${API_BASE}/user-settings`;
const DEFAULT_TRANSPORT = 'Vägtransport';

function SettingsPage() {
  const { currentCompany, user: ctxUser, refreshAuth } = useContext(AuthContext);

  const normLower = (v) => (v ? String(v).trim().toLowerCase() : null);
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    if (typeof refreshAuth === 'function') {
      refreshAuth();
    }
  }, [refreshAuth]);

  const effectiveCompanyId = useMemo(() => {
    const cFromUser = toNum(ctxUser?.company_id);
    if (cFromUser) return cFromUser;

    const cFromCtx = toNum(currentCompany?.id);
    if (cFromCtx) return cFromCtx;

    try {
      const raw = localStorage.getItem('currentCompany');
      if (!raw) return null;
      const obj = JSON.parse(raw);
      return toNum(obj?.id) || null;
    } catch {
      return null;
    }
  }, [ctxUser?.company_id, currentCompany?.id]);

  const userId = ctxUser?.id ? toNum(ctxUser.id) : null;
  const userEmail = ctxUser?.email ? normLower(ctxUser.email) : null;

  const nsCompany = effectiveCompanyId ?? 'anonCompany';
  const nsUser = userId ?? userEmail ?? 'anonUser';

  const draftKey = useMemo(() => `settingsDraft:${nsCompany}:${nsUser}`, [nsCompany, nsUser]);
  const legacyKey = useMemo(() => `grunduppgifter:${nsCompany}:${nsUser}`, [nsCompany, nsUser]);
  const logoKey = useMemo(() => `companyLogo:${nsCompany}`, [nsCompany]);
  const placesKey = useMemo(() => `mottagningsplatser:${nsCompany}:${nsUser}`, [nsCompany, nsUser]);
  const deliveredPlacesKey = useMemo(
    () => `lamnplatser:${nsCompany}:${nsUser}`,
    [nsCompany, nsUser]
  );

  const identityReady = Boolean(effectiveCompanyId) && Boolean(userId);

  const emptyForm = useMemo(() => ({
    personnummer: '',
    fornamn: '',
    efternamn: '',
    uppgiftsEmail: '',
    uppgiftsTelefon: '',
    orgnummer: '',
    verksamhetsnamn: '',
    address: '',
    postal_code: '',
    city: '',
    kontaktperson: '',
    kontaktEmail: '',
    kontaktTelefon: '',
    apiNyckel: '',
    cfar: '',
    transportsatt: DEFAULT_TRANSPORT,

    platsNamn: '',
    platsGatuadress: '',
    platsAdress: '',
    platsPostnummer: '',
    platsKommun: '',
    platsX: '',
    platsY: '',
    adressEllerKoordinater: 'adress',
    platsEmail: '',

    lamnPlatsNamn: '',
    lamnPlatsGatuadress: '',
    lamnPlatsAdress: '',
    lamnPlatsPostnummer: '',
    lamnPlatsKommun: '',
    lamnPlatsX: '',
    lamnPlatsY: '',
    lamnAdressEllerKoordinater: 'adress',
    lamnPlatsEmail: '',
  }), []);

  const [form, setForm] = useState(emptyForm);
  const [platser, setPlatser] = useState([]);
  const [platserLamnades, setPlatserLamnades] = useState([]);
  const [logo, setLogo] = useState('');
  const fileInputRef = useRef();
  const hasHydratedRef = useRef(false);
  const justSavedRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  const [extraCompanies, setExtraCompanies] = useState([]);
  const [selectedCompanyKey, setSelectedCompanyKey] = useState('main');

  const canEditPersonal = !isSaving;
  const canEditCompany = Boolean(effectiveCompanyId) && !isSaving;
  const canSubmit = identityReady && !isSaving;

  async function loadExtraCompanies() {
    try {
      if (!effectiveCompanyId) {
        setExtraCompanies([]);
        return;
      }

      const res = await fetch(`${API_BASE}/company-entities?companyId=${effectiveCompanyId}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });

      const raw = await res.text();
      let data = [];

      try {
        data = raw ? JSON.parse(raw) : [];
      } catch {
        throw new Error(`HTTP ${res.status}`);
      }

      if (!res.ok || data?.error) {
        throw new Error(data?.error || 'Kunde inte läsa extra bolag');
      }

      const normalized = Array.isArray(data)
        ? data.map((company) => ({
            id: company.id,
            orgNumber: company.org_number || '',
            companyName: company.name || '',
            verksamhetsnamn: company.name || '',
            address: company.address || '',
            gatuadress: company.address || '',
            postalCode: company.postal_code || '',
            postnummer: company.postal_code || '',
            city: company.city || '',
            ort: company.city || '',
            contactPerson: company.contact_person || '',
            kontaktperson: company.contact_person || '',
            contactEmail: company.contact_email || '',
            kontaktEmail: company.contact_email || '',
            contactPhone: company.phone || '',
            kontaktTelefon: company.phone || '',
            isDefault: Boolean(company.is_default),
          }))
        : [];

      setExtraCompanies(normalized);
    } catch (e) {
      console.error('Kunde inte läsa extraCompanies:', e);
      setExtraCompanies([]);
    }
  }

  function applySelectedCompanyToForm(selectedKey, companiesList = extraCompanies) {
    if (selectedKey === 'main') return;

    const selected = companiesList.find((c) => String(c.id) === String(selectedKey));
    if (!selected) return;

    setForm((prev) => ({
      ...prev,
      orgnummer: selected.orgNumber || '',
      verksamhetsnamn: selected.verksamhetsnamn || selected.companyName || '',
      address: selected.gatuadress || selected.address || '',
      postal_code: selected.postnummer || selected.postalCode || '',
      city: selected.ort || selected.city || '',
      kontaktperson: selected.kontaktperson || selected.contactPerson || '',
      kontaktEmail: selected.kontaktEmail || selected.contactEmail || '',
      kontaktTelefon: selected.kontaktTelefon || selected.contactPhone || '',
    }));
  }

  function handleSelectedCompanyChange(e) {
    const value = e.target.value;
    setSelectedCompanyKey(value);
    applySelectedCompanyToForm(value);
  }

  useEffect(() => {
    if (!identityReady) return;

    hasHydratedRef.current = false;
    setSelectedCompanyKey('main');

    try {
      localStorage.removeItem('grunduppgifter');
    } catch {}
  }, [identityReady, nsCompany, nsUser]);

  useEffect(() => {
    if (!identityReady) return;

    const savedLogo =
      localStorage.getItem(logoKey) ||
      localStorage.getItem('companyLogo');

    if (savedLogo) setLogo(savedLogo);

    try {
      const raw =
        localStorage.getItem(placesKey) ||
        localStorage.getItem('mottagningsplatser');

      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setPlatser(parsed);
          if (!localStorage.getItem(placesKey)) {
            localStorage.setItem(placesKey, raw);
          }
        }
      }
    } catch {}

    try {
      const rawDelivered = localStorage.getItem(deliveredPlacesKey);
      if (rawDelivered) {
        const parsedDelivered = JSON.parse(rawDelivered);
        if (Array.isArray(parsedDelivered)) {
          setPlatserLamnades(parsedDelivered);
        }
      }
    } catch (e) {
      console.warn('Kunde inte läsa lamnplatser från localStorage', e);
    }

    loadExtraCompanies();
  }, [identityReady, logoKey, placesKey, deliveredPlacesKey, effectiveCompanyId]);

  useEffect(() => {
    const onFocus = () => loadExtraCompanies();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [effectiveCompanyId]);

  useEffect(() => {
    if (!effectiveCompanyId || !identityReady) return;
    if (justSavedRef.current) return;

    const ctl = new AbortController();

    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/company-settings?companyId=${effectiveCompanyId}`,
          {
            signal: ctl.signal,
            credentials: 'include',
          }
        );

        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Kunde inte hämta företagsinställningar');

        const hasCompanyData =
          Boolean(data?.org_number) ||
          Boolean(data?.address) ||
          Boolean(data?.postal_code) ||
          Boolean(data?.city) ||
          Boolean(data?.contact_person) ||
          Boolean(data?.contact_email) ||
          Boolean(data?.api_key) ||
          Boolean(data?.verksamhetsnamn) ||
          Boolean(data?.cfar) ||
          Array.isArray(data?.saved_places_delivered);

        if (hasHydratedRef.current && !hasCompanyData) {
          return;
        }

        console.log('[SettingsPage] GET company-settings FULL:', JSON.stringify(data, null, 2));

        const vName =
          data.verksamhetsnamn ??
          currentCompany?.company_name ??
          currentCompany?.companyName ??
          '';

        let localDeliveredPlaces = [];
        try {
          const rawLocal = localStorage.getItem(deliveredPlacesKey);
          if (rawLocal) {
            const parsedLocal = JSON.parse(rawLocal);
            if (Array.isArray(parsedLocal)) {
              localDeliveredPlaces = parsedLocal;
            }
          }
        } catch {}

        const dbDeliveredPlaces = Array.isArray(data.saved_places_delivered)
          ? data.saved_places_delivered
          : [];

        const finalDeliveredPlaces =
          dbDeliveredPlaces.length > 0 ? dbDeliveredPlaces : localDeliveredPlaces;

        setPlatserLamnades(finalDeliveredPlaces);

        try {
          localStorage.setItem(
            deliveredPlacesKey,
            JSON.stringify(finalDeliveredPlaces)
          );
        } catch {}

        setForm((prev) => ({
          ...prev,
          orgnummer: data.org_number ?? prev.orgnummer,
          address: data.address ?? prev.address,
          postal_code: data.postal_code ?? prev.postal_code,
          city: data.city ?? prev.city,
          kontaktperson: data.contact_person ?? prev.kontaktperson,
          kontaktEmail: data.contact_email ?? prev.kontaktEmail,
          kontaktTelefon: data.kontaktTelefon ?? data.kontakttelefon ?? prev.kontaktTelefon,
          apiNyckel: data.api_key ?? prev.apiNyckel,
          verksamhetsnamn: vName || prev.verksamhetsnamn,
          cfar: data.cfar ?? prev.cfar,
          transportsatt: data.transportsatt || prev.transportsatt || DEFAULT_TRANSPORT,
        }));

        if (data.logo_url) {
          setLogo(data.logo_url);
          try {
            localStorage.setItem(logoKey, data.logo_url);
            localStorage.setItem('companyLogo', data.logo_url);
          } catch (e) {
            console.warn('Kunde inte spara företagslogga lokalt', e);
          }
        }

        hasHydratedRef.current = true;
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error('Ladda server company-settings:', e.message);
        }
      }
    })();

    return () => ctl.abort();
  }, [
    identityReady,
    effectiveCompanyId,
    currentCompany?.company_name,
    currentCompany?.companyName,
    logoKey,
    deliveredPlacesKey,
  ]);

  useEffect(() => {
    if (!identityReady || !effectiveCompanyId || !userId) return;
    if (justSavedRef.current) return;

    const ctl = new AbortController();

    (async () => {
      try {
        const qp = new URLSearchParams({
          companyId: String(effectiveCompanyId),
          user_id: String(userId),
        });

        const res = await fetch(`${USER_SETTINGS_URL}?${qp.toString()}`, {
          signal: ctl.signal,
          credentials: 'include',
        });

        const json = await res.json();
        if (!res.ok) throw new Error(json?.error || 'Kunde inte hämta user-settings');

        const hasUserData =
          Boolean(json?.data?.personnummer) ||
          Boolean(json?.data?.fornamn) ||
          Boolean(json?.data?.efternamn) ||
          Boolean(json?.data?.uppgiftsemail) ||
          Boolean(json?.data?.phone) ||
          Array.isArray(json?.data?.saved_places);

        if (hasHydratedRef.current && !hasUserData) {
          return;
        }

        console.log('[SettingsPage] GET user-settings FULL:', JSON.stringify(json, null, 2));

        if (json?.data) {
          const u = json.data;

          let localSavedPlaces = [];
          try {
            const rawLocalPlaces = localStorage.getItem(placesKey);
            if (rawLocalPlaces) {
              const parsedLocalPlaces = JSON.parse(rawLocalPlaces);
              if (Array.isArray(parsedLocalPlaces)) {
                localSavedPlaces = parsedLocalPlaces;
              }
            }
          } catch {}

          const dbSavedPlaces = Array.isArray(u.saved_places) ? u.saved_places : [];
          const finalSavedPlaces =
            dbSavedPlaces.length > 0 ? dbSavedPlaces : localSavedPlaces;

          setPlatser(finalSavedPlaces);
          try {
            localStorage.setItem(placesKey, JSON.stringify(finalSavedPlaces));
          } catch {}

          setForm((prev) => ({
            ...prev,
            personnummer: u.personnummer ?? prev.personnummer,
            fornamn: u.fornamn ?? prev.fornamn,
            efternamn: u.efternamn ?? prev.efternamn,
            uppgiftsEmail: u.uppgiftsemail ?? prev.uppgiftsEmail,
            uppgiftsTelefon: u.phone ?? prev.uppgiftsTelefon,
            transportsatt: u.transportsatt ?? prev.transportsatt ?? DEFAULT_TRANSPORT,
          }));

          hasHydratedRef.current = true;
        } else {
          return;
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.error('Ladda server user-settings:', e.message);
      }
    })();

    return () => ctl.abort();
  }, [identityReady, userId, effectiveCompanyId, placesKey]);

  useEffect(() => {
    if (!identityReady) return;
    const t = setTimeout(() => {
      saveJSON(draftKey, form);
      saveJSON(legacyKey, form);
    }, 350);
    return () => clearTimeout(t);
  }, [identityReady, form, draftKey, legacyKey]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      setLogo(dataUrl);
      try {
        localStorage.setItem(logoKey, dataUrl);
        localStorage.setItem('companyLogo', dataUrl);
      } catch (err) {
        console.warn('Kunde inte spara företagslogga i localStorage', err);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSelectLogo = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveLogo = () => {
    setLogo('');
    try {
      localStorage.removeItem(logoKey);
      localStorage.removeItem('companyLogo');
    } catch (err) {
      console.warn('Kunde inte ta bort företagslogga ur localStorage', err);
    }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleAdressEllerKoordinaterChange = (e) => {
    setForm({
      ...form,
      adressEllerKoordinater: e.target.value,
      platsNamn: '',
      platsGatuadress: '',
      platsAdress: '',
      platsPostnummer: '',
      platsKommun: '',
      platsX: '',
      platsY: '',
      platsEmail: '',
    });
  };

  const handleLamnAdressEllerKoordinaterChange = (e) => {
    setForm({
      ...form,
      lamnAdressEllerKoordinater: e.target.value,
      lamnPlatsNamn: '',
      lamnPlatsGatuadress: '',
      lamnPlatsAdress: '',
      lamnPlatsPostnummer: '',
      lamnPlatsKommun: '',
      lamnPlatsX: '',
      lamnPlatsY: '',
      lamnPlatsEmail: '',
    });
  };

  const handleAddPlats = (e) => {
    e.preventDefault();

    const emailVal = normLower(form.platsEmail) || '';
    if (emailVal && !emailRegex.test(emailVal)) {
      alert('Ange en giltig e-postadress.');
      return;
    }

    let nyPlats = {};
    if (form.adressEllerKoordinater === 'adress') {
      if (
        !form.platsNamn.trim() ||
        !form.platsGatuadress.trim() ||
        !form.platsAdress.trim() ||
        !form.platsPostnummer.trim() ||
        !form.platsKommun.trim()
      ) {
        alert('Fyll i namn, gatuadress, postadress, postnummer och kommun.');
        return;
      }

      nyPlats = {
        id: Date.now(),
        namn: form.platsNamn,
        gatuadress: form.platsGatuadress,
        adress: form.platsAdress,
        postnummer: form.platsPostnummer,
        kommun: form.platsKommun,
        x: '',
        y: '',
        typ: 'adress',
        email: emailVal,
      };
    } else {
      if (!form.platsNamn.trim() || !form.platsX.trim() || !form.platsY.trim()) {
        alert('Fyll i namn och båda koordinaterna (X & Y) för platsen.');
        return;
      }

      nyPlats = {
        id: Date.now(),
        namn: form.platsNamn,
        gatuadress: '',
        adress: '',
        postnummer: '',
        kommun: '',
        x: form.platsX,
        y: form.platsY,
        typ: 'koordinater',
        email: emailVal,
      };
    }

    const nyaPlatser = [...platser, nyPlats];
    setPlatser(nyaPlatser);

    try {
      localStorage.setItem(placesKey, JSON.stringify(nyaPlatser));
    } catch {}

    setForm({
      ...form,
      platsNamn: '',
      platsGatuadress: '',
      platsAdress: '',
      platsPostnummer: '',
      platsKommun: '',
      platsX: '',
      platsY: '',
      platsEmail: '',
      adressEllerKoordinater: 'adress',
    });
  };

  const handleRemovePlats = (id) => {
    const nyaPlatser = platser.filter((plats) => plats.id !== id);
    setPlatser(nyaPlatser);
    try {
      localStorage.setItem(placesKey, JSON.stringify(nyaPlatser));
    } catch {}
  };

  const handleAddPlatsLamnades = (e) => {
    e.preventDefault();

    const emailVal = normLower(form.lamnPlatsEmail) || '';
    if (emailVal && !emailRegex.test(emailVal)) {
      alert('Ange en giltig e-postadress.');
      return;
    }

    let nyPlats = {};
    if (form.lamnAdressEllerKoordinater === 'adress') {
      if (
        !form.lamnPlatsNamn.trim() ||
        !form.lamnPlatsGatuadress.trim() ||
        !form.lamnPlatsAdress.trim() ||
        !form.lamnPlatsPostnummer.trim() ||
        !form.lamnPlatsKommun.trim()
      ) {
        alert('Fyll i namn, gatuadress, postadress, postnummer och kommun.');
        return;
      }

      nyPlats = {
        id: Date.now(),
        namn: form.lamnPlatsNamn,
        gatuadress: form.lamnPlatsGatuadress,
        adress: form.lamnPlatsAdress,
        postnummer: form.lamnPlatsPostnummer,
        kommun: form.lamnPlatsKommun,
        x: '',
        y: '',
        typ: 'adress',
        email: emailVal,
      };
    } else {
      if (!form.lamnPlatsNamn.trim() || !form.lamnPlatsX.trim() || !form.lamnPlatsY.trim()) {
        alert('Fyll i namn och båda koordinaterna (X & Y) för platsen.');
        return;
      }

      nyPlats = {
        id: Date.now(),
        namn: form.lamnPlatsNamn,
        gatuadress: '',
        adress: '',
        postnummer: '',
        kommun: '',
        x: form.lamnPlatsX,
        y: form.lamnPlatsY,
        typ: 'koordinater',
        email: emailVal,
      };
    }

    const nya = [...platserLamnades, nyPlats];
    setPlatserLamnades(nya);

    console.log('[SettingsPage] nya platserLamnades efter Lägg till:', JSON.stringify(nya, null, 2));

    try {
      localStorage.setItem(deliveredPlacesKey, JSON.stringify(nya));
    } catch (err) {
      console.warn('Kunde inte spara lamnplatser lokalt', err);
    }

    setForm({
      ...form,
      lamnPlatsNamn: '',
      lamnPlatsGatuadress: '',
      lamnPlatsAdress: '',
      lamnPlatsPostnummer: '',
      lamnPlatsKommun: '',
      lamnPlatsX: '',
      lamnPlatsY: '',
      lamnPlatsEmail: '',
      lamnAdressEllerKoordinater: 'adress',
    });
  };

  const handleRemovePlatsLamnades = (id) => {
    const nya = platserLamnades.filter((p) => p.id !== id);
    setPlatserLamnades(nya);

    try {
      localStorage.setItem(deliveredPlacesKey, JSON.stringify(nya));
    } catch (err) {
      console.warn('Kunde inte uppdatera lamnplatser lokalt', err);
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (isSaving) return;

    try {
      if (!identityReady) throw new Error('Identitet ej klar (company/user)');
      if (!effectiveCompanyId) throw new Error('Ingen inloggad companyId');
      if (!userId) throw new Error('Ingen inloggad userId');

      const requiredMissing = [];
      if (!form.orgnummer.trim()) requiredMissing.push('Organisationsnummer');
      if (!form.verksamhetsnamn.trim()) requiredMissing.push('Verksamhetens namn');
      if (!form.kontaktperson.trim()) requiredMissing.push('Kontaktperson (företag)');
      if (!form.kontaktEmail.trim()) requiredMissing.push('E-post (kontaktperson)');
      if (!form.kontaktTelefon.trim()) requiredMissing.push('Telefon (kontaktperson)');
      if (!form.personnummer.trim()) requiredMissing.push('Personnummer (uppgiftslämnare)');
      if (!form.fornamn.trim()) requiredMissing.push('Förnamn (uppgiftslämnare)');
      if (!form.efternamn.trim()) requiredMissing.push('Efternamn (uppgiftslämnare)');
      if (!form.uppgiftsEmail.trim()) requiredMissing.push('E-post (uppgiftslämnare)');
      if (!form.uppgiftsTelefon.trim()) requiredMissing.push('Telefon (uppgiftslämnare)');

      if (requiredMissing.length > 0) {
        alert(
          'För att kunna skicka anteckningar till Naturvårdsverket behöver följande fält fyllas i:\n\n' +
          requiredMissing.join('\n')
        );
        return;
      }

      justSavedRef.current = true;
      setIsSaving(true);

      const S = (v) => (v === undefined || v === null ? '' : String(v).trim());

      const userPayload = {
        companyId: effectiveCompanyId,
        user_id: Number(userId),
        personnummer: S(form.personnummer),
        fornamn: S(form.fornamn),
        efternamn: S(form.efternamn),
        phone: S(form.uppgiftsTelefon),
        kontaktTelefon: S(form.kontaktTelefon),
        uppgiftsEmail: S(form.uppgiftsEmail),
        transportsatt: S(form.transportsatt),
        saved_places: platser ?? [],
      };

      const companyPayload = {
        companyId: effectiveCompanyId,
        org_number: S(form.orgnummer),
        address: S(form.address),
        postal_code: S(form.postal_code),
        city: S(form.city),
        contact_person: S(form.kontaktperson),
        contact_email: S(form.kontaktEmail),
        kontaktTelefon: S(form.kontaktTelefon),
        api_key: S(form.apiNyckel),
        verksamhetsnamn: S(form.verksamhetsnamn),
        cfar: S(form.cfar),
        saved_places_delivered: platserLamnades ?? [],
      };

      console.log('[SettingsPage] platserLamnades före save:', JSON.stringify(platserLamnades, null, 2));
      console.log('[SettingsPage] skickar userPayload FULL:', JSON.stringify(userPayload, null, 2));
      console.log('[SettingsPage] skickar companyPayload FULL:', JSON.stringify(companyPayload, null, 2));

      const [r1, r2] = await Promise.all([
        fetch(USER_SETTINGS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(userPayload),
        }),
        fetch(`${API_BASE}/company-settings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(companyPayload),
        }),
      ]);

      const [j1, j2] = await Promise.all([r1.json(), r2.json()]);

      console.log('[SettingsPage] svar user-settings FULL:', JSON.stringify(j1, null, 2));
      console.log('[SettingsPage] svar company-settings FULL:', JSON.stringify(j2, null, 2));

      if (!r1.ok || j1?.error || j1?.ok === false) {
        throw new Error(j1?.error || 'Kunde inte spara personliga inställningar');
      }

      if (!r2.ok || j2?.error || j2?.success === false) {
        throw new Error(j2?.error || 'Kunde inte spara företagsinställningar');
      }

      const savedDeliveredPlaces =
        Array.isArray(j2?.settings?.saved_places_delivered) &&
        j2.settings.saved_places_delivered.length > 0
          ? j2.settings.saved_places_delivered
          : platserLamnades;

      setPlatserLamnades(savedDeliveredPlaces);

      try {
        localStorage.setItem(placesKey, JSON.stringify(platser ?? []));
        localStorage.setItem(
          deliveredPlacesKey,
          JSON.stringify(savedDeliveredPlaces ?? [])
        );
      } catch {}

      removeKey(draftKey);

      if (j2?.warning) {
        alert(`Grunduppgifter sparade, men med varning:\n\n${j2.warning}`);
      } else {
        alert('Grunduppgifter sparade (personligt + företag)!');
      }
    } catch (err) {
      console.error('Spara grunduppgifter:', err);
      alert(`Kunde inte spara: ${err.message}`);
    } finally {
      setIsSaving(false);
      setTimeout(() => {
        justSavedRef.current = false;
      }, 1500);
    }
  }

  return (
    <div className="settings-container">
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: "1.2rem",
        }}
      >
        <BackToDashboard mode="back" />
      </div>

      {!identityReady && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            background: "#fff3cd",
            border: "1px solid #ffe69c",
            borderRadius: 8,
            color: "#664d03",
            fontSize: 14,
          }}
        >
          Klart att fylla i! 👌 Jag sparar först när din identitet är laddad
          (company/user).
        </div>
      )}

      <div
        className="logo-uploader"
        style={{
          marginBottom: "1.7rem",
          display: "flex",
          alignItems: "center",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <strong>Företagslogga: </strong>
        </div>
        {logo ? (
          <img
            src={logo}
            alt="Företagslogga"
            style={{
              width: 48,
              height: 48,
              borderRadius: 8,
              background: "#f2f2f2",
              border: "1px solid #ddd",
              objectFit: "contain",
            }}
          />
        ) : (
          <span style={{ color: "#888", marginLeft: 8 }}>Ingen logga vald</span>
        )}
        <button
          type="button"
          onClick={handleSelectLogo}
          disabled={!canEditCompany}
        >
          Välj logga
        </button>
        {logo && (
          <button
            type="button"
            onClick={handleRemoveLogo}
            style={{ color: "#b00" }}
          >
            Ta bort logga
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleLogoChange}
        />
      </div>

      <h2 className="settings-title">Grunduppgifter</h2>
      <div className="settings-form-outer">
        <form className="settings-form-wrapper" onSubmit={handleSubmit}>
          <fieldset className="section-group">
            <legend className="section-title">
              Uppgiftslämnare <span>(person som signerar, används mot NV)</span>
            </legend>
            <div className="input-row">
              <label>Personnummer</label>
              <input name="personnummer" value={form.personnummer} onChange={handleChange} disabled={!canEditPersonal} />
            </div>
            <div className="input-row">
              <label>Förnamn</label>
              <input name="fornamn" value={form.fornamn} onChange={handleChange} disabled={!canEditPersonal} />
            </div>
            <div className="input-row">
              <label>Efternamn</label>
              <input name="efternamn" value={form.efternamn} onChange={handleChange} disabled={!canEditPersonal} />
            </div>
            <div className="input-row">
              <label>E-post</label>
              <input name="uppgiftsEmail" value={form.uppgiftsEmail} onChange={handleChange} disabled={!canEditPersonal} />
            </div>
            <div className="input-row">
              <label>Telefon</label>
              <input name="uppgiftsTelefon" value={form.uppgiftsTelefon} onChange={handleChange} disabled={!canEditPersonal} />
            </div>
          </fieldset>

          <fieldset className="section-group">
            <legend className="section-title">
              Verksamhetsutövare <span>(företag, uppgifter som skickas till NV)</span>
            </legend>

            <div className="input-row">
              <label>Välj bolag</label>
              <select
                value={selectedCompanyKey}
                onChange={handleSelectedCompanyChange}
                disabled={!canEditCompany}
              >
                <option value="main">Huvudbolag</option>
                {extraCompanies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.verksamhetsnamn || company.companyName || 'Namnlöst bolag'}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-row">
              <label>Organisationsnummer</label>
              <input name="orgnummer" value={form.orgnummer} onChange={handleChange} disabled={!canEditCompany} />
            </div>

            <div className="input-row">
              <label>Verksamhetens namn</label>
              <input name="verksamhetsnamn" value={form.verksamhetsnamn} onChange={handleChange} disabled={!canEditCompany} />
            </div>

            <div className="input-row">
              <label>Gatuadress</label>
              <input name="address" value={form.address} onChange={handleChange} placeholder="Exempelgatan 1" disabled={!canEditCompany} />
            </div>

            <div className="input-row">
              <label>Postnummer</label>
              <input name="postal_code" value={form.postal_code} onChange={handleChange} placeholder="XXXXX" disabled={!canEditCompany} />
            </div>

            <div className="input-row">
              <label>Ort</label>
              <input name="city" value={form.city} onChange={handleChange} placeholder="Exempelstad" disabled={!canEditCompany} />
            </div>

            <div className="input-row">
              <label>Kontaktperson</label>
              <input name="kontaktperson" value={form.kontaktperson} onChange={handleChange} disabled={!canEditCompany} />
            </div>

            <div className="input-row">
              <label>E-post (kontaktperson)</label>
              <input name="kontaktEmail" value={form.kontaktEmail} onChange={handleChange} disabled={!canEditCompany} />
            </div>

            <div className="input-row">
              <label>Telefon (kontaktperson)</label>
              <input name="kontaktTelefon" value={form.kontaktTelefon} onChange={handleChange} disabled={!canEditCompany} />
            </div>
          </fieldset>

          <fieldset className="section-group">
            <legend className="section-title">Transportsätt</legend>
            <div className="input-row">
              <label>Transportsätt</label>
              <select
                name="transportsatt"
                value={form.transportsatt}
                onChange={handleChange}
                disabled={!canEditPersonal}
              >
                {[
                  "Vägtransport",
                  "Järnvägstransport",
                  "Sjötransport",
                  "Flygtransport",
                  "Inlandssjöfart",
                  "Rörledning",
                ].map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </fieldset>

          <fieldset className="section-group">
            <legend className="section-title">
              Plats där avfallet senast hanterades
            </legend>

            {platser.length > 0 && (
              <ul style={{ marginBottom: 12, paddingLeft: 18 }}>
                {platser.map((plats) => (
                  <li key={plats.id} style={{ marginBottom: 5 }}>
                    <strong>{plats.namn}</strong>
                    {plats.typ === "adress" ? (
                      <>
                        {plats.gatuadress ? ` – ${plats.gatuadress}` : ""}
                        {plats.adress
                          ? plats.gatuadress
                            ? `, ${plats.adress}`
                            : ` – ${plats.adress}`
                          : ""}
                        {plats.postnummer || plats.kommun
                          ? `, ${plats.postnummer ?? ""} ${plats.kommun ?? ""}`
                          : ""}
                      </>
                    ) : (
                      <> – Koordinater: X: {plats.x}, Y: {plats.y}</>
                    )}
                    {(plats.email || plats.epost) ? ` – E-post: ${plats.email || plats.epost}` : ""}

                    <button
                      type="button"
                      onClick={() => handleRemovePlats(plats.id)}
                      style={{
                        marginLeft: 8,
                        color: "#b00",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                      disabled={!canEditPersonal}
                    >
                      Ta bort
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div style={{ marginBottom: 10, marginTop: 12 }}>
              <label>
                <input
                  type="radio"
                  name="adressEllerKoordinater"
                  value="adress"
                  checked={form.adressEllerKoordinater === "adress"}
                  onChange={handleAdressEllerKoordinaterChange}
                  style={{ marginRight: 4 }}
                  disabled={!canEditPersonal}
                />
                Ange adress
              </label>
              <label style={{ marginLeft: 16 }}>
                <input
                  type="radio"
                  name="adressEllerKoordinater"
                  value="koordinater"
                  checked={form.adressEllerKoordinater === "koordinater"}
                  onChange={handleAdressEllerKoordinaterChange}
                  style={{ marginRight: 4 }}
                  disabled={!canEditPersonal}
                />
                Ange koordinater (SWEREF 99 TM)
              </label>
            </div>

            {form.adressEllerKoordinater === "adress" && (
              <>
                <div className="input-row">
                  <label>Namn</label>
                  <input name="platsNamn" value={form.platsNamn} onChange={handleChange} disabled={!canEditPersonal} />
                </div>
                <div className="input-row">
                  <label>Gatuadress</label>
                  <input name="platsGatuadress" value={form.platsGatuadress} onChange={handleChange} disabled={!canEditPersonal} />
                </div>
                <div className="input-row">
                  <label>Postadress</label>
                  <input name="platsAdress" value={form.platsAdress} onChange={handleChange} disabled={!canEditPersonal} />
                </div>
                <div className="input-row">
                  <label>Postnummer</label>
                  <input name="platsPostnummer" value={form.platsPostnummer} onChange={handleChange} disabled={!canEditPersonal} />
                </div>
                <div className="input-row">
                  <label>Kommun</label>
                  <input name="platsKommun" value={form.platsKommun} onChange={handleChange} disabled={!canEditPersonal} />
                </div>
              </>
            )}

            {form.adressEllerKoordinater === "koordinater" && (
              <>
                <div className="input-row">
                  <label>Namn</label>
                  <input name="platsNamn" value={form.platsNamn} onChange={handleChange} disabled={!canEditPersonal} />
                </div>
                <div className="input-row">
                  <label>X (Norring SWEREF 99 TM)</label>
                  <input name="platsX" value={form.platsX} onChange={handleChange} placeholder="ex: 6400000" disabled={!canEditPersonal} />
                </div>
                <div className="input-row">
                  <label>Y (Östring SWEREF 99 TM)</label>
                  <input name="platsY" value={form.platsY} onChange={handleChange} placeholder="ex: 130000" disabled={!canEditPersonal} />
                </div>
              </>
            )}

            <div className="input-row">
              <label>E-post (avfallet hanterades)</label>
              <input
                name="platsEmail"
                value={form.platsEmail}
                onChange={handleChange}
                placeholder="mottagare@exempel.se"
                disabled={!canEditPersonal}
              />
            </div>

            <button
              type="button"
              className="settings-btn-secondary"
              style={{ marginTop: 8, marginBottom: 8 }}
              onClick={handleAddPlats}
              disabled={!canEditPersonal}
            >
              Lägg till plats
            </button>
          </fieldset>

          <fieldset className="section-group">
            <legend className="section-title">
              Plats där avfallet lämnas
            </legend>

            {platserLamnades.length > 0 && (
              <ul style={{ marginBottom: 12, paddingLeft: 18 }}>
                {platserLamnades.map((plats) => (
                  <li key={plats.id} style={{ marginBottom: 5 }}>
                    <strong>{plats.namn}</strong>
                    {plats.typ === "adress" ? (
                      <>
                        {plats.gatuadress ? ` – ${plats.gatuadress}` : ""}
                        {plats.adress
                          ? plats.gatuadress
                            ? `, ${plats.adress}`
                            : ` – ${plats.adress}`
                          : ""}
                        {plats.postnummer || plats.kommun
                          ? `, ${plats.postnummer ?? ""} ${plats.kommun ?? ""}`
                          : ""}
                      </>
                    ) : (
                      <> – Koordinater: X: {plats.x}, Y: {plats.y}</>
                    )}
                    {(plats.email || plats.epost) ? ` – E-post: ${plats.email || plats.epost}` : ""}

                    <button
                      type="button"
                      onClick={() => handleRemovePlatsLamnades(plats.id)}
                      style={{
                        marginLeft: 8,
                        color: "#b00",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                      disabled={!canEditPersonal}
                    >
                      Ta bort
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div style={{ marginBottom: 10, marginTop: 12 }}>
              <label>
                <input
                  type="radio"
                  name="lamnAdressEllerKoordinater"
                  value="adress"
                  checked={form.lamnAdressEllerKoordinater === "adress"}
                  onChange={handleLamnAdressEllerKoordinaterChange}
                  style={{ marginRight: 4 }}
                  disabled={!canEditPersonal}
                />
                Ange adress
              </label>
              <label style={{ marginLeft: 16 }}>
                <input
                  type="radio"
                  name="lamnAdressEllerKoordinater"
                  value="koordinater"
                  checked={form.lamnAdressEllerKoordinater === "koordinater"}
                  onChange={handleLamnAdressEllerKoordinaterChange}
                  style={{ marginRight: 4 }}
                  disabled={!canEditPersonal}
                />
                Ange koordinater (SWEREF 99 TM)
              </label>
            </div>

            {form.lamnAdressEllerKoordinater === "adress" && (
              <>
                <div className="input-row">
                  <label>Namn</label>
                  <input name="lamnPlatsNamn" value={form.lamnPlatsNamn} onChange={handleChange} disabled={!canEditPersonal} />
                </div>
                <div className="input-row">
                  <label>Gatuadress</label>
                  <input name="lamnPlatsGatuadress" value={form.lamnPlatsGatuadress} onChange={handleChange} disabled={!canEditPersonal} />
                </div>
                <div className="input-row">
                  <label>Postadress</label>
                  <input name="lamnPlatsAdress" value={form.lamnPlatsAdress} onChange={handleChange} disabled={!canEditPersonal} />
                </div>
                <div className="input-row">
                  <label>Postnummer</label>
                  <input name="lamnPlatsPostnummer" value={form.lamnPlatsPostnummer} onChange={handleChange} disabled={!canEditPersonal} />
                </div>
                <div className="input-row">
                  <label>Kommun</label>
                  <input name="lamnPlatsKommun" value={form.lamnPlatsKommun} onChange={handleChange} disabled={!canEditPersonal} />
                </div>
              </>
            )}

            {form.lamnAdressEllerKoordinater === "koordinater" && (
              <>
                <div className="input-row">
                  <label>Namn</label>
                  <input name="lamnPlatsNamn" value={form.lamnPlatsNamn} onChange={handleChange} disabled={!canEditPersonal} />
                </div>
                <div className="input-row">
                  <label>X (Norring SWEREF 99 TM)</label>
                  <input name="lamnPlatsX" value={form.lamnPlatsX} onChange={handleChange} placeholder="ex: 6400000" disabled={!canEditPersonal} />
                </div>
                <div className="input-row">
                  <label>Y (Östring SWEREF 99 TM)</label>
                  <input name="lamnPlatsY" value={form.lamnPlatsY} onChange={handleChange} placeholder="ex: 130000" disabled={!canEditPersonal} />
                </div>
              </>
            )}

            <div className="input-row">
              <label>E-post (avfallet lämnas)</label>
              <input
                name="lamnPlatsEmail"
                value={form.lamnPlatsEmail}
                onChange={handleChange}
                placeholder="mottagare@exempel.se"
                disabled={!canEditPersonal}
              />
            </div>

            <button
              type="button"
              className="settings-btn-secondary"
              style={{ marginTop: 8, marginBottom: 8 }}
              onClick={handleAddPlatsLamnades}
              disabled={!canEditPersonal}
            >
              Lägg till plats
            </button>
          </fieldset>

          <button
            className="settings-btn-primary"
            type="submit"
            disabled={!canSubmit}
          >
            {isSaving ? "Sparar…" : "Spara grunduppgifter"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SettingsPage;